import { Pipe, PipeTransform } from '@angular/core';

export type ByteUnit = 'B' | 'kB' | 'KB' | 'MB' | 'GB' | 'TB';

type ByteFormat = { max: number; prev?: ByteUnit };
type ByteFormats = Record<ByteUnit, ByteFormat>;

@Pipe({
  name: 'bytes',
  standalone: true
})
export class BytesPipe implements PipeTransform {
  public static formats: ByteFormats = {
    B: { max: 1024 },
    kB: { max: Math.pow(1024, 2), prev: 'B' },
    KB: { max: Math.pow(1024, 2), prev: 'B' }, // Backward compatible
    MB: { max: Math.pow(1024, 3), prev: 'kB' },
    GB: { max: Math.pow(1024, 4), prev: 'MB' },
    TB: { max: Number.MAX_SAFE_INTEGER, prev: 'GB' }
  };

  public static formatResult(result: number, unit: ByteUnit): string {
    return `${result} ${unit}`;
  }

  public static calculateResult(format: ByteFormat, bytes: number): number {
    const prevUnit: ByteUnit | undefined = format.prev;
    const prev: ByteFormat | undefined = prevUnit !== undefined ? BytesPipe.formats[prevUnit] : undefined;

    return prev !== undefined ? bytes / prev.max : bytes;
  }

  public toDecimal(value: number, decimal: number): number {
    return Math.round(value * Math.pow(10, decimal)) / Math.pow(10, decimal);
  }

  public transform(input: number, decimal: number = 0, from: ByteUnit = 'B', to?: ByteUnit): string {
    let bytes: number = input;
    let unit: ByteUnit = from;

    while (unit !== 'B') {
      bytes *= 1024;
      const prevUnit: ByteUnit | undefined = BytesPipe.formats[unit].prev;
      if (prevUnit === undefined) {
        break;
      }
      unit = prevUnit;
    }

    if (to !== undefined) {
      const format: ByteFormat = BytesPipe.formats[to];
      const result: number = this.toDecimal(BytesPipe.calculateResult(format, bytes), decimal);
      return BytesPipe.formatResult(result, to);
    }

    const units: readonly ByteUnit[] = ['B', 'kB', 'KB', 'MB', 'GB', 'TB'] as const;

    for (const key of units) {
      const format: ByteFormat = BytesPipe.formats[key];
      if (bytes < format.max) {
        const result: number = this.toDecimal(BytesPipe.calculateResult(format, bytes), decimal);
        return BytesPipe.formatResult(result, key);
      }
    }

    const fallback: ByteUnit = 'TB';
    const fallbackResult: number = this.toDecimal(BytesPipe.calculateResult(BytesPipe.formats[fallback], bytes), decimal);
    return BytesPipe.formatResult(fallbackResult, fallback);
  }
}
